import React, { useState, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { FaChevronDown } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import LazyImage from "./LazyImage";

const Dropdown = React.memo(
  ({
    label,
    options = [],
    placeholder = "Select",
    error,
    name,
    id,
    onChange,
    onCancel,
    onCheck,
    value,
    width,
    closeBtn,
    search = false,
    required = false,
    checkBox = false,
    checkValue = false,
    disabled = false,
    textColor = false,
  }) => {
    // console.log("checkValue",checkValue);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const [searchTerm, setSearchTerm] = useState("");
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);

    /* ---------- close menu when clicking elsewhere ---------- */
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (
          !buttonRef.current?.contains(e.target) &&
          !dropdownRef.current?.contains(e.target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mouseup", handleClickOutside);
      return () => document.removeEventListener("mouseup", handleClickOutside);
    }, []);

    /* ---------- clear search box when menu closes ---------- */
    useEffect(() => {
      if (!isOpen) setSearchTerm("");
    }, [isOpen]);


    useEffect(() => {
      if (!isOpen) return;

      const handleScrollClose = (e) => {
        // If scroll happened inside the dropdown, don't close
        if (dropdownRef.current?.contains(e.target)) return;
        setIsOpen(false);
      };

      window.addEventListener("scroll", handleScrollClose, true);

      return () => {
        window.removeEventListener("scroll", handleScrollClose, true);
      };
    }, [isOpen]);


    /* ---------- position the floating list ---------- */
useLayoutEffect(() => {
  if (!isOpen) return;

  const updateDropdownPosition = () => {
    if (!buttonRef.current || !dropdownRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Decide where to place the menu
    const placeBelow = spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove;

    setDropdownPos({
      top: placeBelow
        ? rect.bottom + window.scrollY + 5
        : rect.top + window.scrollY - dropdownHeight - 5,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
  };

  updateDropdownPosition();                     // initial
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);

  return () => {
    window.removeEventListener("scroll", updateDropdownPosition, true);
    window.removeEventListener("resize", updateDropdownPosition);
  };
}, [isOpen]);

    /* ---------- filter options ---------- */
    const filteredOptions = useMemo(() => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return options;
      return Array.isArray(options) && options.filter(
        (opt) => opt.label?.toLowerCase().includes(term) || opt.value?.toString().includes(term)
      );
    }, [options, searchTerm]);

    /* ---------- option handlers ---------- */
    const handleSelect = (event, option) => {
      setIsOpen(false);
      const syntheticEvent = {
        ...event,
        target: { ...event.target, label, name, value: option.value, option },
      };
      // console.log("syntheticEvent", syntheticEvent);
      if (!option?.disable) onChange?.(syntheticEvent, option);
    };

    const handleClose = (event, option) => {
      event.stopPropagation();
      setIsOpen(false);
      const syntheticEvent = {
        ...event,
        target: { ...event.target, label, name, value: "" },
      };
      if (!option?.disable) onCancel?.(syntheticEvent, option);
    };
    const handleCheckBox = (event) => {
      event.stopPropagation();
      const syntheticEvent = {
        ...event,
        target: { ...event.target, label, name, value: "" },
      };
      onCheck?.(syntheticEvent);
    };

    /* ---------- render ---------- */
    return (
      <>
        <div id={id} name={name} className={`relative ${width ?? ""}`}>
          {label && (
            <label
              htmlFor={name}
              className={`flex text-left font-medium ${textColor ? "text-blue-700" : "text-gray-700"} mb-1`}
            >
              {label}
              {required && (
                <span className="bg-red-100 text-red-600 rounded-full relative w-3.5 h-3.5">
                  <span className=" absolute top-[-2px] left-[3px]">*</span>
                </span>
              )}
            </label>
          )}

          <button
            ref={buttonRef}
            type="button"
            disabled={disabled}
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center justify-between w-full px-3 py-2.5 border 
              ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white" } ${ options?.find((val) => val?.value === value)?.backgroundColor || "" }
              border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
          >
            <span
              className={`${
                options?.find((val) => val?.value === value)?.color || ""
              } truncate overflow-hidden whitespace-nowrap flex-1 text-left`}
            >
              {options?.find((val) => val?.value === value)?.label || placeholder}
            </span>

            {/* {options?.find((val) => val?.value === value)?.label &&
              options?.find((val) => val?.value === value)?.value !== value && */}
            {closeBtn && options?.find((val) => val?.value === value)?.label &&
            !options?.find((val) => val?.value === value)?.disable && (
                <ImCross
                  onClick={handleClose}
                  className="ml-2 text-gray-300 hover:text-gray-500 text-sm flex-shrink-0"
                />
              )}

            <FaChevronDown className="ml-2 text-blue-800 flex-shrink-0" />
          </button>
          {error && (<div className=" text-red-700 text-sm font-medium text-left">{error}</div>)}
        </div>

        {isOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              className="z-50 bg-white border border-gray-300 rounded shadow-md max-h-64 overflow-auto text-sm"
              style={{
                position: "absolute",
                top: `${dropdownPos.top}px`,
                left: `${dropdownPos.left}px`,
                width: `${dropdownPos.width}px`,
              }}
            >
              {/* search box */}
              {search && (
                <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
                    <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="w-full rounded-md px-2 py-1 focus:outline-none border border-blue-500 focus:ring-1 focus:ring-blue-500"
                    autoFocus
                    />
                </div>
              )}
              {checkBox && (
                <div className={`sticky ${search ? "top-10" : "top-0"} bg-white p-2 border-b border-gray-200`}>
                  <div className="flex justify-start items-center gap-2">
                    <input
                    id="check"
                    type="checkbox"
                    checked={checkValue}
                    onChange={handleCheckBox}
                    className="w-fit rounded-md border-gray-300 px-2 py-1 focus:outline-none cursor-pointer"
                    />
                    <label htmlFor="check" className=" cursor-pointer">Active</label>
                  </div>
                </div>
              )}

              {/* results */}
              <ul>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <li
                      key={option.value}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(e, option);
                      }}
                      className={`px-3 py-2 ${
                        option?.color ? option?.color : "text-gray-700"
                      } ${
                        !option?.disable
                          ? "hover:bg-lime-50 cursor-pointer"
                          : "bg-gray-200 cursor-not-allowed"
                      }`}
                    >
                      {option.label}
                    </li>
                  ))
                ) : (
                  <li className="px-3 py-2 text-gray-700 text-center">
                    No matching options
                  </li>
                )}
              </ul>
            </div>,
            document.body
          )}
      </>
    );
  }
);


export default Dropdown;


export const CurrencyDropdown = React.memo(
  ({
    label,
    options = [],
    placeholder = "None",
    name,
    id,
    onChange,
    value,
    error,
    disabled = false,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null); 

    useEffect(() => {
    const handleClickOutside = (e) => {
        if ( !buttonRef.current?.contains(e.target) &&
      !dropdownRef.current?.contains(e.target)) {
        setIsOpen(false);
        }
    };

    document.addEventListener("mouseup", handleClickOutside);
    return () => document.removeEventListener("mouseup", handleClickOutside);
    }, []);

    useEffect(() => {
      const updateDropdownPosition = () => {
        if (isOpen && buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();

          const isVisible =
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth);

          if (!isVisible) {
            setIsOpen(false);
            return;
          }

          setDropdownPos({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      };

      if (isOpen) {
        window.addEventListener("scroll", updateDropdownPosition, true);
        window.addEventListener("resize", updateDropdownPosition);
        updateDropdownPosition();
      }

      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }, [isOpen]);

    const handleSelect = (event, option) => {
        // console.log("===>>>",option);
      setIsOpen(false);

      const syntheticEvent = {
        ...event,
        target: {
          ...event.target,
          label,
          name,
          value: option.value,
        },
      };
      
      onChange?.(syntheticEvent, option);
    };
    // console.log("isOpen ===>>",isOpen);
    return (
      <>
        <div id={id} name={name} className="relative ">
          {label && (
            <label htmlFor={name} className="block text-left font-medium text-gray-700 mb-1">
              {label}
            </label>
          )}
        <button
            ref={buttonRef}
            type="button"
            disabled={disabled}
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center justify-between ${disabled && "bg-gray-100"} gap-2 w-[8rem] px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
            {options?.find(val => val?.value === value) && (<LazyImage className="w-5" src={options?.find(val => val?.value === value)?.img} alt={options?.find(val => val?.value === value)?.code} />)}
            <span className="truncate overflow-hidden whitespace-nowrap flex-1 text-left">
                { options?.find(val => val?.value === value)?.label || placeholder}
            </span>
            <FaChevronDown className="text-sm text-blue-800 flex-shrink-0" />
        </button>
        {error && (<div className=" text-red-700 text-sm font-medium text-left">{error}</div>)}
        </div>

        {isOpen &&
          createPortal(
            <ul
                ref={dropdownRef}
                className="z-50 min-w-16 max-w-54 bg-white border border-gray-300 rounded shadow-md max-h-60 overflow-auto text-sm"
                style={{ position: "absolute", top: `${dropdownPos.top + 5}px`, left: `${dropdownPos.left}px`, }}
            >
              {Array.isArray(options) && options.length > 0 ? (
                options.map((option) => (
                  <li
                    key={option.code}
                    onClick={(e) => { e.stopPropagation(); handleSelect(e, option)}}
                    className=" flex items-center flex-nowrap gap-2 px-3 py-2 cursor-pointer hover:bg-lime-100 text-gray-700"
                  >
                    <LazyImage className="w-5" src={option?.img} alt={option?.label} />
                    {`${option.label} (${option?.symbol} ${option?.code})`}
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-gray-700 text-center">No Options</li>
              )}
            </ul>, document.body)}
      </>
    );
  }
);